import { useSortable } from '@dnd-kit/sortable';
import type { Transform } from '@dnd-kit/utilities';
import { CSS } from '@dnd-kit/utilities';
import type { MantineTheme } from '@mantine/core';
import { Box, createStyles, Group, Stack } from '@mantine/core';
import { Icon, Switch, Text } from '@repo/foundations';
import type React from 'react';

export type IDraggableMenuItemProps = {
	value: string;
	label: string;
	disableDragging?: boolean;
	height?: number;
	index: number;
	onVisibilityChange?: (isVisible: boolean) => void;
	isVisible?: boolean;
	suffix?: React.ReactNode;
	subtext?: React.ReactNode;
	rightContent?: React.ReactNode;
	leftContent?: React.ReactNode;
};

const getItemStyle = (
	theme: MantineTheme,
	isDragging: boolean,
	transform?: Transform | null,
	transition?: string | null
): React.CSSProperties => ({
	// Styles we need to apply on draggables
	transform: transform ? CSS.Transform.toString(transform) : undefined,
	transition: transition || undefined,

	// Some basic styles to make the items look a bit nicer
	userSelect: 'none',

	// Change background color if dragging
	backgroundColor: isDragging
		? theme.other.getColor('surface/primary/hover')
		: undefined,
});

const useStyles = createStyles(
	(theme, { height = 52 }: { height?: number }) => ({
		draggableWrapper: {
			cursor: 'move',
		},
		wrapper: {
			height,
			paddingTop: 0,
			paddingBottom: 0,
			paddingRight: theme.spacing.xs,
			paddingLeft: 2,
			borderRadius: theme.radius.sm,
			backgroundColor: theme.other.getColor('surface/primary/default'),
			'&:hover': {
				backgroundColor: theme.other.getColor('surface/primary/hover'),
				'.mantine-Icon-icon': {
					color: theme.other.getColor('icon/secondary/hover'),
					stroke: theme.other.getColor('icon/secondary/hover'),
				},
			},
		},
		emptyMenuWrapper: {},
		label: {
			flex: '1 1 auto',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			maxWidth: '20ch',
		},
	})
);

export function DraggableMenuItem({
	value,
	label,
	height = 32,
	index,
	onVisibilityChange,
	subtext,
	isVisible,
	suffix,
	disableDragging = false,
	rightContent,
	leftContent,
}: IDraggableMenuItemProps) {
	const { classes, cx, theme } = useStyles({ height });

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: value,
		disabled: disableDragging,
	});

	const handleVisibilityChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		onVisibilityChange?.(event.target.checked);
	};

	return (
		<Group
			ref={setNodeRef}
			className={cx(classes.wrapper, {
				[classes.draggableWrapper]: !disableDragging,
			})}
			noWrap
			align="center"
			style={getItemStyle(theme, isDragging, transform, transition)}
			position="apart"
			{...attributes}
			{...listeners}
		>
			<Group spacing="xs" noWrap>
				{!disableDragging && (
					<Icon
						p={0}
						color={
							isDragging ? 'icon/secondary/active' : 'icon/secondary/default'
						}
						name="gripVertical"
						style={{ cursor: 'grab' }}
					/>
				)}
				<Stack spacing={0}>
					{leftContent ? (
						leftContent
					) : (
						<Text className={classes.label} size="sm">
							{label}
						</Text>
					)}
					{subtext}
				</Stack>
			</Group>
			<Group spacing={0}>
				{suffix && <Box>{suffix}</Box>}
				{rightContent ? (
					rightContent
				) : (
					<Switch
						size="sm"
						onChange={handleVisibilityChange}
						checked={isVisible}
						onPointerDown={(e) => e.stopPropagation()}
						onPointerUp={(e) => e.stopPropagation()}
					/>
				)}
			</Group>
		</Group>
	);
}
